import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, LogOut, Mail, Calendar, Loader2, Shield } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import AppSidebar from '../components/AppSidebar';
import { VoicePicker } from '../components/VoicePicker';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';

const Account = () => {
  const { t, lang } = useApp();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const a = t.account;
  const isPro = user.plan === 'pro';
  const end = user.current_period_end ? new Date(user.current_period_end).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  const act = async (fn, msg) => {
    setBusy(true);
    try { setUser({ ...user, ...(await fn()) }); toast.success(msg); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="flex bg-background" data-testid="account-page">
      <AppSidebar conversations={[]} onNewChat={() => navigate('/chat')} />
      <main className="flex-1 h-screen overflow-y-auto min-w-0">
        <div className="h-16 border-b border-border flex items-center px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur z-10">
          <h1 className="font-display text-xl font-bold">{a.title}</h1>
        </div>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6 flex items-center gap-4">
            {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl object-cover" /> : <span className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center font-display text-xl font-bold">{user.name?.[0]?.toUpperCase()}</span>}
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold text-lg truncate" data-testid="account-name">{user.name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate"><Mail className="h-3.5 w-3.5" /> {user.email}</p>
            </div>
            <span data-testid="account-plan-badge" className={`text-xs font-bold px-2.5 py-1 rounded-full ${isPro ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>{isPro ? 'PRO' : a.free}</span>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Crown className="h-5 w-5 text-primary" /> {a.subscription}</h2>
            {isPro ? (
              <div className="mt-4 space-y-3 text-sm">
                <p className="text-muted-foreground">{a.proActive} {user.plan_interval === 'yearly' ? a.yearlyPlan : a.monthlyPlan}.</p>
                {end && <p className="flex items-center gap-2 text-muted-foreground"><Calendar className="h-4 w-4" /> {user.cancel_at_period_end ? a.endsOn : a.renewsOn} <strong className="text-foreground">{end}</strong></p>}
                {user.subscription_status === 'past_due' && <p className="text-destructive">{a.pastDue}</p>}
                <div className="pt-2">
                  {user.cancel_at_period_end ? (
                    <Button data-testid="resume-sub-btn" onClick={() => navigate('/upgrade')} className="rounded-xl font-semibold">{a.resume}</Button>
                  ) : (
                    <Button disabled={busy} variant="outline" data-testid="cancel-sub-btn" onClick={() => act(api.cancelSubscription, a.cancelled)} className="rounded-xl font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : a.cancel}</Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-4 text-sm">
                <p className="text-muted-foreground">{a.usage.replace('{used}', user.usage_today ?? 0).replace('{limit}', user.free_daily_limit)}</p>
                <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, ((user.usage_today ?? 0) / user.free_daily_limit) * 100)}%` }} /></div>
                <Button onClick={() => navigate('/upgrade')} data-testid="account-upgrade-btn" className="rounded-xl font-semibold gap-2"><Crown className="h-4 w-4" /> {a.upgrade}</Button>
              </div>
            )}
          </div>

          <VoicePicker />

          <Button variant="ghost" data-testid="logout-btn" onClick={() => { navigate('/'); logout(); }} className="rounded-xl font-semibold gap-2 text-muted-foreground"><LogOut className="h-4 w-4" /> {a.logout}</Button>
          {user.role === 'admin' && <Button variant="outline" data-testid="account-admin-btn" onClick={() => navigate('/admin')} className="ml-2 rounded-xl font-semibold gap-2"><Shield className="h-4 w-4" /> {t.admin.title}</Button>}
        </div>
      </main>
    </div>
  );
};

export default Account;
